import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Cube, Clock, Waveform, Target, ChartLineUp, Newspaper,
  Pulse, Lightning, ShieldCheckered,
} from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { fetchEngines } from "@/lib/api";
import { AUREUS } from "@/constants/testIds";

const ICON_BY_ID = {
  smc: Cube,
  ict: Clock,
  order_flow: Waveform,
  liquidity: Target,
  session: ChartLineUp,
  news: Newspaper,
  volatility: Pulse,
  momentum: Lightning,
  risk: ShieldCheckered,
};

function StatusDot({ status }) {
  const cls =
    status === "live" ? "bg-bull animate-pulse-dot" :
    status === "warming" ? "bg-amber-400 animate-pulse-dot" : "bg-bear";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
}

export default function EnginesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["engines"],
    queryFn: fetchEngines,
    refetchInterval: 6000,
  });

  return (
    <AppShell>
      <div className="p-6 md:p-10" data-testid={AUREUS.enginesPage}>
        <header className="mb-8">
          <div className="overline mb-3">Engine Room</div>
          <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter">
            Nine engines. <span className="text-gold-grad">Always running.</span>
          </h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Each engine is monitored independently. Risk engine has final say
            on every Trade Brief — including auto-downgrades.
          </p>
        </header>

        {isLoading && <div className="surface h-[500px] animate-pulse" />}
        {error && <div className="surface p-6 text-bear">{String(error.message || error)}</div>}

        {data?.engines && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-white/10 bg-ink-800">
            {data.engines.map((e) => {
              const Icon = ICON_BY_ID[e.id] || Pulse;
              return (
                <div
                  key={e.id}
                  data-testid={AUREUS.engineCard(e.id)}
                  className="border-b border-r border-white/10 p-7 min-h-[200px] flex flex-col hover:bg-white/[0.025] transition"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={26} className="text-gold" weight="light" />
                    <div className="flex items-center gap-2">
                      <StatusDot status={e.status} />
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">{e.status}</span>
                    </div>
                  </div>
                  <h3 className="heading text-xl font-semibold tracking-tight mt-5">
                    {e.name}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1">{e.tagline}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between">
                    <span className="overline">{e.metric.label}</span>
                    <span className="mono text-lg text-white">{e.metric.value}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
